import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

const ArrowLeftIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const ClockIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const HintBulbIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlSpace="preserve"
    viewBox="0 0 512 512"
    fill="#fff"
    width="26"
    height="26"
  >
    <path d="M183.9 491.5v-53.2h144.4v53.2c-23.9 14.3-48.1 21.2-72.7 20.5-23.9.6-47.8-6.2-71.7-20.5m141.3-84H187c0-24.6-6.1-48.5-18.4-71.7q-18.45-34.8-39.9-57.9c-14.3-15.4-27-34.5-37.9-57.3S75.4 174 77.5 149.4c2.7-41 18.8-76.1 48.1-105.5s72.7-44 130-44c58 0 101.5 14.7 130.5 44 29 29.4 45.2 64.5 48.6 105.5 1.4 20.5-1.4 39.8-8.2 57.9s-15.7 34.5-26.6 49.1q-16.35 22.05-33.3 43.5c-16.95 21.45-21 30.7-29.2 49.1-8.1 18.6-12.2 38-12.2 58.5M131.7 153.6q-2.1 2.1 0 10.2c1.4 5.5 1.7 8.9 1 10.2s.2 4.6 2.6 9.7 3.4 8.2 3.1 9.2 1 4.1 4.1 9.2 4.9 8.4 5.6 9.7c.7 1.4 2.9 4.6 6.7 9.7s6.1 8.4 7.2 9.7c1 1.4 3.6 4.9 7.7 10.8s6.8 9.7 8.2 11.8c30 41.6 49.1 77.8 57.3 108.5h42c8.2-32.1 27.3-68.3 57.3-108.5 1.4-2 5.6-8 12.8-17.9s11.4-16 12.8-18.4 4.3-7.3 8.7-14.8 7.2-13.1 8.2-16.9 2-8.5 3.1-14.3 1.2-11.8.5-17.9c-5.6-66.9-47.2-100.4-125-100.4-77.2 0-118.5 33.5-123.9 100.4" />
  </svg>
);

const RuleBlock = React.forwardRef(function RuleBlock(
  { active, children, noBorder = false },
  ref
) {
  return (
    <Box
      ref={ref}
      sx={{
        py: "40px",
        borderBottom: noBorder ? "none" : "1px solid rgba(255,255,255,0.05)",
        opacity: active ? 1 : 0.5,
        transition: "opacity 0.5s ease",
      }}
    >
      {children}
    </Box>
  );
});

const Rules = ({ onClose }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const ruleRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (!visibleEntries.length) return;

        visibleEntries.sort(
          (a, b) => b.intersectionRatio - a.intersectionRatio
        );

        const matchedIndex = ruleRefs.current.findIndex(
          (node) => node === visibleEntries[0].target
        );

        if (matchedIndex !== -1) {
          setActiveIndex(matchedIndex);
        }
      },
      {
        root: null,
        rootMargin: "-20% 0px -40% 0px",
        threshold: [0.1, 0.2, 0.35, 0.5, 0.75],
      }
    );

    const nodes = ruleRefs.current.filter(Boolean);
    nodes.forEach((node) => observer.observe(node));

    return () => {
      nodes.forEach((node) => observer.unobserve(node));
      observer.disconnect();
    };
  }, []);

  const assignRuleRef = (index) => (el) => {
    ruleRefs.current[index] = el;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Montserrat:wght@400;600;700&family=Lora:ital,wght@0,400;1,400&family=Space+Mono:wght@400;700&display=swap');

        @keyframes pulse-hint-white {
          0% { filter: drop-shadow(0 0 2px rgba(255,255,255,0.3)); }
          50% { filter: drop-shadow(0 0 10px rgba(255,255,255,0.8)); }
          100% { filter: drop-shadow(0 0 2px rgba(255,255,255,0.3)); }
        }
      `}</style>

   <Box
  sx={{
    width: "100%",
    px: { xs: 0, sm: 2 },
    py: { xs: 0, sm: 3 },
    boxSizing: "border-box",
    color: "#eaeaea",
    fontFamily: "'Montserrat', sans-serif",
  }}
>
      <Box
  sx={{
    maxWidth: { xs: "100%", sm: "550px" },
    width: "100%",
    mx: "auto",
    my: { xs: 0, sm: 2 },
    minHeight: { xs: "100dvh", sm: "auto" },
    background: "rgba(22, 24, 28, 0.98)",
    borderRadius: { xs: 0, sm: "12px" },
    boxShadow: { xs: "none", sm: "0 20px 50px rgba(0,0,0,0.8)" },
    border: { xs: "none", sm: "1px solid rgba(255,255,255,0.05)" },
    overflow: "hidden",
  }}
>
          <Box
            sx={{
              position: "relative",
              fontFamily: "'Cinzel', serif",
              fontSize: "1.2rem",
              textAlign: "center",
              py: "25px",
              px: "56px",
              color: "#fff",
              letterSpacing: "2px",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              background: "rgba(0,0,0,0.2)",
            }}
          >
            <Box
              onClick={onClose}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onClose?.();
              }}
              sx={{
                position: "absolute",
                left: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: onClose ? "pointer" : "default",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            >
              <ArrowLeftIcon />
            </Box>

            PRAVIDLA HRY
          </Box>

          <Box
            sx={{
              px: { xs: "18px", sm: "25px" },
              pt: 0,
              pb: "30px",
            }}
          >
            <RuleBlock ref={assignRuleRef(0)} active={activeIndex === 0}>
              <Box>
                <Box
                  component="h3"
                  sx={{
                    fontSize: "1.2rem",
                    color: "#fff",
                    mt: 0,
                    mb: "15px",
                    fontWeight: 700,
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "1px",
                  }}
                >
                  1. Vstup do příběhu
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Každý příběh začíná unikátním kódem. Jakmile jej zadáte,
                  odemknete hru. Kliknutím na tlačítko{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    Vstoupit do příběhu
                  </Box>{" "}
                  pak odjistíte časomíru a hra začne.
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: "'Cinzel', serif",
                      fontSize: "1.4rem",
                      letterSpacing: "3px",
                      color: "#fff",
                      mb: "10px",
                      textAlign: "center",
                      mt: "10px",
                    }}
                  >
                    PŘÍBĚH
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      mb: "30px",
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: "'Space Mono', monospace",
                        fontSize: "0.75rem",
                        letterSpacing: "2px",
                        color: "rgba(255, 255, 255, 0.5)",
                        position: "relative",
                        px: "10px",
                        "&::before, &::after": {
                          content: '""',
                          display: "block",
                          width: "30px",
                          height: "1px",
                          background: "rgba(255,255,255,0.2)",
                          position: "absolute",
                          top: "50%",
                          transform: "translateY(-50%)",
                        },
                        "&::before": { left: "-35px" },
                        "&::after": { right: "-35px" },
                      }}
                    >
                      BREAKTHEMYSTERY
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      fontFamily: "'Cinzel', serif",
                      px: "20px",
                      py: "12px",
                      borderRadius: "25px",
                      fontSize: "0.85rem",
                      letterSpacing: "2px",
                      width: "100%",
                      maxWidth: "320px",
                      textAlign: "center",
                      my: "5px",
                      boxSizing: "border-box",
                    }}
                  >
                    VSTOUPIT DO PŘÍBĚHU
                  </Box>
                </Box>
              </Box>
            </RuleBlock>

            <RuleBlock ref={assignRuleRef(1)} active={activeIndex === 1}>
              <Box>
                <Box
                  component="h3"
                  sx={{
                    fontSize: "1.2rem",
                    color: "#fff",
                    mt: 0,
                    mb: "15px",
                    fontWeight: 700,
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "1px",
                  }}
                >
                  2. Hodnocení a Leaderboard
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Do příběhu vstupujete s plným počtem{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    10 hvězdiček
                  </Box>
                  . Samotná hra není nijak časově omezená, můžete si tak příběh
                  užívat ve vlastním tempu.
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Váš čas se sice počítá, ale slouží primárně k tomu, abyste
                  mohli své výsledky (jak čas, tak i hvězdičky) porovnat s
                  ostatními hráči v sekci{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    Leaderboard
                  </Box>
                  .
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "4px",
                      color: "#e5b022",
                      fontSize: "1rem",
                      mt: "5px",
                      mb: 0,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    ★ ★ ★ ★ ★ ★ ★ ★ ★ ★
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                      fontSize: "0.9rem",
                      mt: "5px",
                      color: "rgba(255,255,255,0.6)",
                    }}
                  >
                    <ClockIcon size={14} />
                    00:45:12
                  </Box>
                </Box>

                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      mb: "20px",
                      fontFamily: "'Cinzel', serif",
                      fontSize: "1rem",
                      letterSpacing: "3px",
                      color: "#fff",
                      "&::before, &::after": {
                        content: '""',
                        flexGrow: 1,
                        height: "1px",
                        background: "rgba(255,255,255,0.2)",
                        mx: "15px",
                      },
                    }}
                  >
                    TVOJE POZICE
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #28442c",
                      borderRadius: "8px",
                      px: "15px",
                      py: "12px",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      boxSizing: "border-box",
                      background: "rgba(0,0,0,0.3)",
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: "0.85rem",
                    }}
                  >
                    <Box
                      sx={{
                        fontWeight: 700,
                        mr: "15px",
                        color: "rgba(255,255,255,0.9)",
                        filter: "blur(4px)",
                        opacity: 0.8,
                      }}
                    >
                      18.
                    </Box>

                    <Box
                      sx={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background: "#444",
                        mr: "15px",
                        flexShrink: 0,
                        filter: "blur(4px)",
                        opacity: 0.8,
                      }}
                    />

                    <Box
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        flexGrow: 1,
                      }}
                    >
                      VY
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: "10px", sm: "15px" },
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          color: "#e5b022",
                          fontWeight: 600,
                          filter: "blur(4px)",
                          opacity: 0.8,
                        }}
                      >
                        ★ 0.0
                      </Box>

                      <Box
                        sx={{
                          color: "rgba(255,255,255,0.6)",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          filter: "blur(4px)",
                          opacity: 0.8,
                        }}
                      >
                        <ClockIcon size={13} />
                        00:24:13
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </RuleBlock>

            <RuleBlock ref={assignRuleRef(2)} active={activeIndex === 2}>
              <Box>
                <Box
                  component="h3"
                  sx={{
                    fontSize: "1.2rem",
                    color: "#fff",
                    mt: 0,
                    mb: "15px",
                    fontWeight: 700,
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "1px",
                  }}
                >
                  3. Systém nápověd
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Pokud se někde zaseknete, pod ikonkou{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    žárovky
                  </Box>{" "}
                  ve spodní části obrazovky najdete záchranné lano.
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Nápovědy jsou stupňované: první dvě vám napoví správný směr,
                  třetí už je přímá odpověď. Každé použití nápovědy (včetně té
                  poslední) vás ale bude stát{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    půl hvězdičky
                  </Box>
                  .
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      mt: "5px",
                      mb: "25px",
                      color: "#e5b022",
                      fontSize: "1rem",
                      gap: "4px",
                      width: "100%",
                    }}
                  >
                    <Box component="span">★ ★ ★ ★ ★ ★ ★</Box>
                    <Box
                      component="span"
                      sx={{
                        background:
                          "linear-gradient(90deg, #e5b022 50%, rgba(255,255,255,0.15) 50%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        display: "inline-block",
                      }}
                    >
                      ★
                    </Box>
                    <Box
                      component="span"
                      sx={{ color: "rgba(255,255,255,0.15)" }}
                    >
                      ★ ★
                    </Box>
                  </Box>

                  {[
                    ["PRVNÍ NÁPOVĚDA", "-0.5 ★", false],
                    ["DRUHÁ NÁPOVĚDA", "-0.5 ★", false],
                    ["ODPOVĚĎ", "-0.5 ★", true],
                  ].map(([label, cost, isAnswer]) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        background: "rgba(30, 30, 30, 0.5)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "6px",
                        px: "15px",
                        py: "12px",
                        mb: "8px",
                        boxSizing: "border-box",
                        fontFamily: "'Lora', serif",
                        fontSize: "0.8rem",
                        letterSpacing: "1px",
                        color: isAnswer ? "#fff" : "rgba(255,255,255,0.7)",
                        textTransform: "uppercase",
                      }}
                    >
                      <Box component="span">{label}</Box>
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          color: "rgba(255,255,255,0.5)",
                          fontSize: "0.8rem",
                          fontFamily: "'Montserrat', sans-serif",
                          fontWeight: 600,
                        }}
                      >
                        {cost}
                      </Box>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pt: "15px",
                      fontSize: "0.85rem",
                      fontFamily: "'Montserrat', sans-serif",
                      borderTop: "1px solid rgba(255,255,255,0.05)",
                      mt: "10px",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        minWidth: 0,
                      }}
                    >
                      ⏱ 43 min 59 s
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "30px",
                        height: "30px",
                        animation: "pulse-hint-white 2s infinite",
                        flexShrink: 0,
                      }}
                    >
                      <HintBulbIcon />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </RuleBlock>

            <RuleBlock ref={assignRuleRef(3)} active={activeIndex === 3}>
              <Box>
                <Box
                  component="h3"
                  sx={{
                    fontSize: "1.2rem",
                    color: "#fff",
                    mt: 0,
                    mb: "15px",
                    fontWeight: 700,
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "1px",
                  }}
                >
                  4. Otázky a tajemné volby
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Při pátrání narazíte na různé překážky.
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  U výběrových otázek (kde může být správná jedna i více
                  odpovědí) buďte opatrní – každý špatný tip znamená{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    ztrátu půl hvězdičky
                  </Box>
                  . U otevřených textových odpovědí naopak můžete zkoušet štěstí
                  beze ztrát.
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  V určitých momentech narazíte také na{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    otázky na čas
                  </Box>
                  . Pokud v limitu nestihnete odpovědět, o hvězdičky sice
                  nepřijdete, ale možná to ovlivní, kudy a jak se bude váš
                  příběh ubírat dál...
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      mb: "15px",
                      color: "#e5e5e5",
                    }}
                  >
                    ⏳ Zbývá: 160s
                  </Box>

                  <Box
                    sx={{
                      fontFamily: "'Lora', serif",
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                      textAlign: "center",
                      mb: "12px",
                      color: "#fff",
                    }}
                  >
                    Kde dohledáte Leaderboard?
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      fontFamily: "'Montserrat', sans-serif",
                      px: "20px",
                      py: "12px",
                      borderRadius: "25px",
                      fontSize: "0.75rem",
                      width: "100%",
                      maxWidth: "320px",
                      textAlign: "center",
                      my: "5px",
                      boxSizing: "border-box",
                      textTransform: "none",
                    }}
                  >
                    A) www.breakthemystery.cz
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #fff",
                      background: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      fontFamily: "'Montserrat', sans-serif",
                      px: "20px",
                      py: "12px",
                      borderRadius: "25px",
                      fontSize: "0.75rem",
                      width: "100%",
                      maxWidth: "320px",
                      textAlign: "center",
                      my: "5px",
                      boxSizing: "border-box",
                      textTransform: "none",
                    }}
                  >
                    B) www.breakthemysterygame.cz
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.1)",
                      background: "rgba(255,255,255,0.05)",
                      color: "#fff",
                      fontFamily: "'Cinzel', serif",
                      px: "20px",
                      py: "12px",
                      borderRadius: "25px",
                      fontSize: "0.85rem",
                      letterSpacing: "1px",
                      width: "100%",
                      maxWidth: "320px",
                      textAlign: "center",
                      mt: "15px",
                      mb: "5px",
                      boxSizing: "border-box",
                    }}
                  >
                    ODESLAT ODPOVĚĎ
                  </Box>
                </Box>

                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: "'Lora', serif",
                      fontSize: "0.8rem",
                      lineHeight: 1.5,
                      textAlign: "center",
                      mb: "10px",
                      color: "#d1d1d1",
                    }}
                  >
                    Kdy vychází první escape box Break The Mystery?
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(20,20,20,0.8)",
                      color: "rgba(255,255,255,0.3)",
                      fontFamily: "'Montserrat', sans-serif",
                      px: "20px",
                      py: "12px",
                      borderRadius: "25px",
                      fontSize: "0.85rem",
                      width: "100%",
                      maxWidth: "320px",
                      textAlign: "center",
                      mb: "10px",
                      boxSizing: "border-box",
                    }}
                  >
                    ZADEJTE ODPOVĚĎ
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.1)",
                      background: "rgba(255,255,255,0.05)",
                      color: "#fff",
                      fontFamily: "'Cinzel', serif",
                      px: "20px",
                      py: "12px",
                      borderRadius: "25px",
                      fontSize: "0.85rem",
                      letterSpacing: "1px",
                      width: "100%",
                      maxWidth: "320px",
                      textAlign: "center",
                      my: "5px",
                      boxSizing: "border-box",
                    }}
                  >
                    ODESLAT ODPOVĚĎ
                  </Box>
                </Box>
              </Box>
            </RuleBlock>
            <RuleBlock ref={assignRuleRef(4)} active={activeIndex === 4}>
  <Box>
    <Box
      component="h3"
      sx={{
        fontSize: "1.2rem",
        color: "#fff",
        mt: 0,
        mb: "15px",
        fontWeight: 700,
        fontFamily: "'Cinzel', serif",
        letterSpacing: "1px",
      }}
    >
      5. Navigace a přesuny
    </Box>

    <Box
      component="p"
      sx={{
        fontSize: "0.95rem",
        lineHeight: 1.7,
        mt: 0,
        mb: "15px",
        color: "rgba(255,255,255,0.85)",
      }}
    >
      Kdykoliv se v průběhu musíte přesunout na novou lokaci, objeví se nad
      tlačítkem pro pokračování ikona{" "}
      <Box component="strong" sx={{ color: "#fff" }}>
        mapy
      </Box>
      .
    </Box>

    <Box
      component="p"
      sx={{
        fontSize: "0.95rem",
        lineHeight: 1.7,
        mt: 0,
        mb: "15px",
        color: "rgba(255,255,255,0.85)",
      }}
    >
      Po jejím rozkliknutí uvidíte{" "}
      <Box component="strong" sx={{ color: "#fff" }}>
        vyznačenou trasu
      </Box>{" "}
      od vašeho aktuálního stanoviště až{" "}
      <Box component="strong" sx={{ color: "#fff" }}>
        do cíle
      </Box>
      . Mapu následně zavřete jednoduchým klepnutím kamkoliv mimo ni.
    </Box>
  </Box>

  <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
    <Box
      sx={{
        background: "rgba(8, 10, 14, 0.85)",
        border: "1px solid rgba(255, 255, 255, 0.07)",
        borderRadius: "12px",
        px: "20px",
        py: "25px",
        boxSizing: "border-box",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backdropFilter: "blur(15px)",
        boxShadow:
          "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
      }}
    >
      <Box
        sx={{
          fontFamily: "'Lora', serif",
          fontSize: "0.85rem",
          lineHeight: 1.5,
          textAlign: "center",
          color: "#b3b3b3",
          mb: "25px",
        }}
      >
        (Klikněte na „Pokračovat“, až dorazíte k cíli.)
      </Box>

      <Box
        sx={{
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: "25px",
          boxShadow:
            "inset 0 2px 5px rgba(255,255,255,0.02), 0 5px 15px rgba(0,0,0,0.4)",
        }}
      >
        <Box
          component="svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{ animation: "pulse-hint-white 2s infinite" }}
        >
          <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
          <line x1="9" y1="3" x2="9" y2="18" />
          <line x1="15" y1="6" x2="15" y2="21" />
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.15)",
          background: "rgba(255,255,255,0.05)",
          color: "#fff",
          fontFamily: "'Cinzel', serif",
          px: "20px",
          py: "12px",
          borderRadius: "25px",
          fontSize: "0.85rem",
          letterSpacing: "1px",
          width: "100%",
          maxWidth: "320px",
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        POKRAČOVAT
      </Box>
    </Box>
  </Box>
</RuleBlock>

            <RuleBlock
              ref={assignRuleRef(5)}
              active={activeIndex === 5}
              noBorder
            >
              <Box>
                <Box
                  component="h3"
                  sx={{
                    fontSize: "1.2rem",
                    color: "#fff",
                    mt: 0,
                    mb: "15px",
                    fontWeight: 700,
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "1px",
                  }}
                >
                  6. Konec příběhu a statistiky
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Jakmile dorazíte k cíli, čeká vás poslední krok: stisknutí
                  tlačítka pro{" "}
                  <Box component="strong" sx={{ color: "#fff" }}>
                    ukončení hry a zastavení časomíry
                  </Box>
                  .
                </Box>

                <Box
                  component="p"
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    mt: 0,
                    mb: "15px",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Váš výsledek se promítne do Leaderboardu a následně získáte
                  přístup ke statistikám, kde uvidíte, jak se v průběhu hry
                  rozhodovali ostatní.
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                <Box
                  sx={{
                    background: "rgba(8, 10, 14, 0.85)",
                    border: "1px solid rgba(255, 255, 255, 0.07)",
                    borderRadius: "12px",
                    px: "20px",
                    py: "25px",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backdropFilter: "blur(15px)",
                    boxShadow:
                      "inset 0 2px 10px rgba(255,255,255,0.02), 0 10px 20px rgba(0,0,0,0.5)",
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.3)",
                      background: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      fontFamily: "'Cinzel', serif",
                      px: { xs: "14px", sm: "15px" },
                      py: "14px",
                      borderRadius: "25px",
                      fontSize: { xs: "0.72rem", sm: "0.8rem" },
                      letterSpacing: "1px",
                      width: "100%",
                      maxWidth: "100%",
                      textAlign: "center",
                      my: "5px",
                      boxSizing: "border-box",
                      whiteSpace: { xs: "normal", sm: "nowrap" },
                    }}
                  >
                    UKONČIT HRU A ZASTAVIT ČASOMÍRU
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      mt: "25px",
                      pt: "15px",
                      borderTop: "1px solid rgba(255,255,255,0.05)",
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: "'Lora', serif",
                        fontSize: "0.8rem",
                        lineHeight: 1.5,
                        textAlign: "center",
                        mb: "10px",
                        color: "#d1d1d1",
                      }}
                    >
                      JAK SE ROZHODOVALI OSTATNÍ
                    </Box>

                    <Box sx={{ width: "100%", mb: "12px" }}>
                      <Box
                        sx={{
                          fontSize: "0.8rem",
                          color: "#fff",
                          mb: "4px",
                          fontWeight: 600,
                        }}
                      >
                        TAK (70%)
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          height: "8px",
                          background: "rgba(255,255,255,0.1)",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: "70%",
                            background: "#888",
                            borderRadius: "4px",
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ width: "100%", mb: "12px" }}>
                      <Box
                        sx={{
                          fontSize: "0.8rem",
                          color: "#fff",
                          mb: "4px",
                          fontWeight: 600,
                        }}
                      >
                        ONAK (30%)
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          height: "8px",
                          background: "rgba(255,255,255,0.1)",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: "30%",
                            background: "#888",
                            borderRadius: "4px",
                            opacity: 0.5,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </RuleBlock>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Rules;